// material-ui
import {
    Box,
    Button, Dialog, DialogActions, DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Typography
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import {useLocation, useNavigate, useParams} from "react-router";
import React, {useEffect, useState} from "react";
import axios from "axios";
import SkeletonPopularCard from 'ui-component/cards/Skeleton/PopularCard';
import Delete from "@mui/icons-material/Delete";
import Add from "@mui/icons-material/Add";
import AnimateButton from "../../ui-component/extended/AnimateButton";
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

// ==============================|| SAMPLE PAGE ||============================== //

const MonitorPage = () => {
    const location = useLocation();
    const {id} = useParams();
    const [loading, setLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [data, setData] = useState({})
    const [prompts, setPrompts] = useState([])
    const [contents, setContents] = useState([])
    const [prompt, setPrompt] = useState()
    const [content, setContent] = useState()
    const [title, setTitle] = useState();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        setData(null);
        setIsSubmitting(false);
        setLoading(false);
        setData({});
        setPrompts([]);
        setContents([]);
        setPrompt(null);
        setContent(null);
        setTitle("");
    }, [location.pathname]);
    
    useEffect(() => {
        if (id) _getData();
    }, [id]);

    function _getData() {
        try {
            const config = {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    'Content-Type': 'application/json',
                }
            }
            setLoading(true);
            const response = axios.get("/api/Monitor/" + id, config);
            response.then(x => {
                setLoading(false);
                setPrompt(null);
                setContent(null);
                setData(x.data);
                setTitle(x.data.title);
                setContents(x.data.contents)
                setPrompts(x.data.prompts)
            })
        } catch (error) {
            console.error("Save failed", error);
        }
    }

    const onDragEnd = (result) => {
        if (!result.destination) return; // dropped outside the list
        const items = Array.from(prompts);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setPrompts(items);
    };

    const handleUpdate =(e)=>{
        setIsSubmitting(true);
        try {
            const config = {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    'Content-Type': 'application/json',
                }
            }
            const response = axios.put("/api/Monitor/" + data.id,
                {
                    contents: contents,
                    prompts: prompts,
                    title: title
                }
                , config);
            response.then(x => {
                setIsSubmitting(false);
                _getData();
            })
        } catch (error) {
            console.error("Save failed", error);
            setIsSubmitting(false);
        }
    }

    const handleCreate =(e)=>{
        setIsSubmitting(true);
        try {
            const config = {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    'Content-Type': 'application/json',
                }
            }
            const response = axios.post("/api/Monitor",{
                contents: contents,
                prompts: prompts,
                title: title
            }, config);
            response.then(x => {
                setIsSubmitting(false);
                navigate("/monitor/monitor/" + x);
            })
        } catch (error) {
            console.error("Save failed", error);
            setIsSubmitting(false);
        }
    }

    const handleAnalyze =(e)=>{
        setIsSubmitting(true);
        try {
            const config = {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    'Content-Type': 'application/json',
                }
            }
            const response = axios.get("/api/Ai/MonitorAnalyze/" + id, config);
            response.then(x => {
                setIsSubmitting(false);
                setDialogMessage(x.data.value); // Assuming x.data.value contains the message
                setDialogOpen(true); // Open the modal
            })
        } catch (error) {
            console.error("Analyze failed", error);
            setIsSubmitting(false);
            setDialogMessage("Analyze failed.");
            setDialogOpen(true); // Open the modal on error
        }
    }

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    return <Box>
        {loading ? (
            <SkeletonPopularCard/>
        ) : (
            <MainCard title={title}>
                <Typography variant="body2">
                    {data.description}
                </Typography>
                <MainCard>
                    <InputLabel htmlFor="outlined-adornment-title">Title</InputLabel>
                    <OutlinedInput
                        id="outlined-adornment-title"
                        value={title}
                        style={{width:'100%'}}
                        name="title"
                        onChange={(e)=>{
                            setTitle(e.target.value);
                        }}
                        label="Title"
                    />
                    
                    <Box sx={{marginTop:2}}/>

                    <InputLabel htmlFor="outlined-adornment-content">Add Content</InputLabel>
                    <OutlinedInput
                        multiline={true}
                        rows={4}
                        style={{width:'100%'}}
                        id="outlined-adornment-content"
                        name="content"
                        onChange={(e)=>{
                            setContent(e.target.value);
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton sx={{mr:1}}
                                            aria-label="toggle content"
                                            onClick={()=>{
                                                const newContents = [...contents, content];
                                                setContents(newContents);
                                            }}
                                            edge="end"
                                            size="large"
                                >
                                    <Add/>
                                </IconButton>
                            </InputAdornment>
                        }
                        label="Contents"
                        inputProps={{}}
                    />

                    <Box sx={{marginTop:2}}/>

                    {contents.map((x, index) => (
                        <>
                            <InputLabel htmlFor={"outlined-adornment-content-" + index}>
                                Content {index + 1}
                            </InputLabel>
                            <OutlinedInput
                                multiline={true}
                                rows={4}
                                style={{ width: '100%' }}
                                id={"outlined-adornment-content-" + index}
                                value={x}
                                onChange={(e) => {
                                    setContents((prevContents) => {
                                        const updatedContents = [...prevContents];
                                        updatedContents[index] = e.target.value;
                                        return updatedContents;
                                    });
                                }}
                                name="contact"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            sx={{ mr: 1 }}
                                            aria-label="toggle prompt visibility"
                                            onClick={() => {
                                                setContents((prevContents) =>
                                                    prevContents.filter((_, i) => i !== index)
                                                );
                                            }}
                                            edge="end"
                                            size="large"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Content"
                                inputProps={{}}
                            />
                        </>
                    ))}
                    
                    <Box sx={{marginTop:2}}/>
                    
                    <InputLabel htmlFor="outlined-adornment-prompt">Add Prompt</InputLabel>
                    <OutlinedInput
                        multiline={true}
                        rows={4}
                        style={{width:'100%'}}
                        id="outlined-adornment-prompt"
                        name="prompt"
                        onChange={(e)=>{
                            setPrompt(e.target.value);
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton sx={{mr:1}}
                                            aria-label="toggle prompt"
                                            onClick={()=>{
                                                const newPrompts = [...prompts, prompt];
                                                setPrompts(newPrompts);
                                            }}
                                            edge="end"
                                            size="large"
                                >
                                    <Add/>
                                </IconButton>
                            </InputAdornment>
                        }
                        label="Prompt"
                        inputProps={{}}
                    />
                </MainCard>
                
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {prompts.map((x, index) => (
                                    <Draggable key={index} draggableId={`draggable-${index}`} index={index}>
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <MainCard>
                                                    <InputLabel htmlFor={"outlined-adornment-prompt-login-" + index}>
                                                        Prompt {index + 1}
                                                    </InputLabel>
                                                    <OutlinedInput
                                                        multiline={true}
                                                        rows={4}
                                                        style={{ width: '100%' }}
                                                        id={"outlined-adornment-prompt-login-" + index}
                                                        value={x}
                                                        onChange={(e) => {
                                                            setPrompts((prevPrompts) => {
                                                                const updatedPrompts = [...prevPrompts];
                                                                updatedPrompts[index] = e.target.value;
                                                                return updatedPrompts;
                                                            });
                                                        }}
                                                        name="prompt"
                                                        endAdornment={
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    sx={{ mr: 1 }}
                                                                    aria-label="toggle prompt visibility"
                                                                    onClick={() => {
                                                                        setPrompts((prevPrompts) =>
                                                                            prevPrompts.filter((_, i) => i !== index)
                                                                        );
                                                                    }}
                                                                    edge="end"
                                                                    size="large"
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        }
                                                        label="prompt"
                                                        inputProps={{}}
                                                    />
                                                </MainCard>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                <Grid container direction="row" justifyContent="between" spacing={2}>
                    <Box sx={{ m: 2, mt:5, width:120 }}>
                        <AnimateButton>
                            {id ?
                                (<Button disableElevation disabled={isSubmitting} fullWidth size="large" type="button" variant="contained" color="secondary" onClick={handleUpdate}>
                                    Update
                                </Button>)
                                :
                                (<Button disableElevation disabled={isSubmitting} fullWidth size="large" type="button" variant="contained" color="secondary" onClick={handleCreate}>
                                    Insert
                                </Button>)
                            }
                        </AnimateButton>
                    </Box>
                    <Box sx={{ m: 2, mt:5, width:120 }}>
                        <AnimateButton>
                            {id &&<Button disableElevation disabled={prompts?.length===0 || isSubmitting} fullWidth size="large" type="button" variant="contained" color="primary" onClick={handleAnalyze}>
                                Analyze
                            </Button>}
                        </AnimateButton>
                    </Box>
                </Grid>
            </MainCard>
        )}
        <Dialog open={dialogOpen} onClose={handleCloseDialog}>
            <DialogTitle>Analysis Result</DialogTitle>
            <DialogContent>
                <Typography>{dialogMessage}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    </Box>
}

export default MonitorPage;
